/* global axios */
import { DataManager } from '../helper/CacheHelper/DataManager';
import ApiClient from './ApiClient';

class CacheEnabledApiClient extends ApiClient {
  constructor(resource, options = {}) {
    super(resource, options);
    this.cacheStore = null;
  }

  // These clients are module singletons, built when the bundle loads. In the
  // PWA that happens at "/", before the router redirects to the account, so the
  // account id is still empty and the cache used to land in a single shared
  // database that mixed the inboxes of every account. Building the manager on
  // demand keeps one database per account.
  get dataManager() {
    const accountId = this.accountIdFromRoute;
    if (!this.cacheStore || this.cacheStore.accountId !== accountId) {
      this.cacheStore = new DataManager(accountId);
    }
    return this.cacheStore;
  }

  // eslint-disable-next-line class-methods-use-this
  get cacheModelName() {
    throw new Error('cacheModelName is not defined');
  }

  get(cache = false) {
    if (cache && this.accountIdFromRoute) {
      return this.getWithCacheFallback();
    }

    return this.getFromNetwork();
  }

  // A blocked/corrupt IndexedDB can leave getFromCache pending forever, which
  // freezes any store that awaits it (e.g. inboxes stuck on isFetching). Race
  // the cache path against a timeout and fall back to the network so a broken
  // local cache degrades gracefully instead of hanging the whole UI.
  getWithCacheFallback() {
    const CACHE_TIMEOUT_MS = 3000;
    const timeout = new Promise((_, reject) => {
      setTimeout(
        () => reject(new Error('IndexedDB cache timeout')),
        CACHE_TIMEOUT_MS
      );
    });

    return Promise.race([this.getFromCache(), timeout]).catch(() =>
      this.getFromNetwork()
    );
  }

  getFromNetwork() {
    return axios.get(this.url);
  }

  // eslint-disable-next-line class-methods-use-this
  extractDataFromResponse(response) {
    return response.data.payload;
  }

  // eslint-disable-next-line class-methods-use-this
  marshallData(dataToParse) {
    return { data: { payload: dataToParse } };
  }

  async getFromCache() {
    try {
      // IDB is not supported in Firefox private mode: https://bugzilla.mozilla.org/show_bug.cgi?id=781982
      await this.dataManager.initDb();
    } catch {
      return this.getFromNetwork();
    }

    const { data } = await axios.get(
      `/api/v1/accounts/${this.accountIdFromRoute}/cache_keys`
    );
    const cacheKeyFromApi = data.cache_keys[this.cacheModelName];
    const isCacheValid = await this.validateCacheKey(cacheKeyFromApi);

    let localData = [];
    if (isCacheValid) {
      localData = await this.dataManager.get({
        modelName: this.cacheModelName,
      });
    }

    if (localData.length === 0) {
      return this.refetchAndCommit(cacheKeyFromApi);
    }

    return this.marshallData(localData);
  }

  async refetchAndCommit(newKey = null) {
    const response = await this.getFromNetwork();

    try {
      await this.dataManager.initDb();

      // The key is what makes the next session trust the cache, so it can only
      // be stored once the records are actually written.
      await this.dataManager.replace({
        modelName: this.cacheModelName,
        data: this.extractDataFromResponse(response),
      });

      await this.dataManager.setCacheKeys({
        [this.cacheModelName]: newKey,
      });
    } catch {
      // Ignore error
    }

    return response;
  }

  async validateCacheKey(cacheKeyFromApi) {
    if (!this.dataManager.db) {
      await this.dataManager.initDb();
    }

    const cachekey = await this.dataManager.getCacheKey(this.cacheModelName);
    return cacheKeyFromApi === cachekey;
  }
}

export default CacheEnabledApiClient;
